import React, {useEffect, useRef, useState} from 'react';
import {
  ActivityIndicator,
  AppState,
  AppStateStatus,
  StatusBar,
  StyleSheet,
  View,
} from 'react-native';
import {SafeAreaProvider, SafeAreaView} from 'react-native-safe-area-context';
import {RoutesTab} from './src/navigation/TabNavigation/HomeTabScreen';
// import SplashScreen from 'react-native-splash-screen';
import LoadingScreen from './src/components/LoadingScren/LoadingScreen';
import {useLoadingStore} from './src/store/LoadingStore';
import DeviceInfo from 'react-native-device-info';
import VersionValidationService from './src/services/VersionValidationService/VersionValidationService';
import AppModalHost from './src/components/AppModalHost/AppModalHost';
import GlobalAlert from './src/components/GlobalAlert/GlobalAlert';
import {useCommunicationModalStore} from './src/store/CommunicationModalStore';
import {OneSignal} from 'react-native-onesignal';
import {useAuthStore} from './src/store/AuthStore';
import ProfileService from './src/services/ProfileService/ProfileService';
import * as Sentry from '@sentry/react-native';
import type {Event as SentryEvent, EventHint as SentryEventHint} from '@sentry/types';
import type {AxiosError} from 'axios';
import {ActionSheetProvider} from '@expo/react-native-action-sheet';
import RNBootSplash from 'react-native-bootsplash';

const HTTP_UNAUTHORIZED = 401;

const isObjectLike = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const extractStatusCandidate = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
      return Number(trimmed);
    }

    const match = trimmed.match(/status(?:\s*code)?\s*(\d{3})/i);
    if (match) {
      return Number(match[1]);
    }
    return undefined;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const candidate = extractStatusCandidate(item);
      if (typeof candidate === 'number') {
        return candidate;
      }
    }
    return undefined;
  }

  if (!isObjectLike(value)) {
    return undefined;
  }

  const objectValue = value as Record<string, unknown>;
  const potentialKeys = ['status', 'statusCode', 'status_code', 'code'];

  for (const key of potentialKeys) {
    if (key in objectValue) {
      const candidate = extractStatusCandidate(objectValue[key]);
      if (typeof candidate === 'number') {
        return candidate;
      }
    }
  }

  if ('response' in objectValue) {
    const candidate = extractStatusCandidate(objectValue.response);
    if (typeof candidate === 'number') {
      return candidate;
    }
  }

  if ('data' in objectValue) {
    const candidate = extractStatusCandidate(objectValue.data);
    if (typeof candidate === 'number') {
      return candidate;
    }
  }

  return undefined;
};

const shouldDrop401Event = (
  event: SentryEvent,
  hint?: SentryEventHint,
): boolean => {
  const statuses = new Set<number>();

  const pushStatus = (value?: number) => {
    if (typeof value === 'number') {
      statuses.add(value);
    }
  };

  if (hint?.originalException) {
    const originalException = hint.originalException as
      | (Partial<AxiosError> & Record<string, unknown>)
      | undefined;
    pushStatus(extractStatusCandidate(originalException));
    if (originalException) {
      pushStatus(extractStatusCandidate(originalException.response));
      pushStatus(extractStatusCandidate(originalException.cause));
    }
  }

  if (event.request) {
    pushStatus(extractStatusCandidate(event.request));
  }

  if (event.contexts?.response) {
    pushStatus(extractStatusCandidate(event.contexts.response));
  }

  if (event.tags) {
    pushStatus(extractStatusCandidate(event.tags));
  }

  if (event.extra) {
    pushStatus(extractStatusCandidate(event.extra));
  }

  if (statuses.has(HTTP_UNAUTHORIZED)) {
    return true;
  }

  if (event.exception?.values?.length) {
    const contains401Message = event.exception.values.some(value => {
      const message = `${value.type ?? ''} ${value.value ?? ''}`
        .toLowerCase()
        .trim();
      if (!message) {
        return false;
      }

      return (
        message.includes('status code 401') ||
        message.includes('code 401') ||
        message.includes('401 unauthorized') ||
        /(^|\W)401(\W|$)/.test(message)
      );
    });

    if (contains401Message) {
      return true;
    }
  }

  return false;
};


if (!__DEV__) {
  // Descomentar quando for gerar apk pra produção
  Sentry.init({
    dsn: 'https://661e18b2239e13243c7b110ea51876eb@o4508774288785408.ingest.us.sentry.io/4508813973848064',
    beforeSend(event, hint) {
      return shouldDrop401Event(event, hint) ? null : event;
    },

    // uncomment the line below to enable Spotlight (https://spotlightjs.com)
    // spotlight: __DEV__,
  });
} else {
  // Desativa o Sentry no ambiente de desenvolvimento
  console.log('Sentry está desativado em ambiente de desenvolvimento');
}
interface VersionData {
  version: string;
  build: number;
  expirationTime: number;
}

interface ApiResponse {
  android: {
    build: number;
    version: string;
  };
  ios: {
    build: number;
    version: string;
  };
}

const App = () => {
  const [showModal, setShowModal] = useState(false);
  const [showBuildProblem, setShowBuildProblem] = useState(false);
  const appVersion = DeviceInfo.getVersion();
  const buildNumber = parseInt(DeviceInfo.getBuildNumber());
  const isLoading = useLoadingStore(state => state.isLoading);
  const {user} = useAuthStore();
  const fetchCommunicationModals = useCommunicationModalStore(state => state.fetchModals);

  const sendOneSignalSubscription = async () => {
    const playerId = await OneSignal.User.pushSubscription.getIdAsync();
    const clientCode = user?.clientCode;
    if (clientCode && playerId) {
      try {
        await ProfileService.createOneSignalSubscription(playerId, clientCode);
      } catch (error) {
        console.error('Failed to create OneSignal subscription ', error);
      }
    }
  };

  OneSignal.initialize('3e4ea4d7-1ed6-46da-97b3-87efb7cb824e');
  OneSignal.Notifications.addEventListener('click', event => {
    console.log('OneSignal: notification clicked:', event);
  });
  useEffect(() => {
    RNBootSplash.hide({ fade: true });
    sendOneSignalSubscription();
  }, []);

  useEffect(() => {
    const checkVersion = async () => {
      const result = await VersionValidationService.validateVersion(
        appVersion,
        buildNumber,
      );
      if (result.hasProblem) {
        setShowBuildProblem(true);
      } else if (result.isOutdated) {
        setShowModal(true);
      }
    };
    checkVersion();
  }, [appVersion, buildNumber]);

  useEffect(() => {
    if (user?.clientCode) {
      fetchCommunicationModals();
    }
  }, [user?.clientCode, fetchCommunicationModals]);

  // As modais do dia são criadas por job às 12:00. Sem isto, quem deixou o app
  // em background de manhã só as veria no próximo cold start.
  const appStateRef = useRef(AppState.currentState);
  useEffect(() => {
    const subscription = AppState.addEventListener(
      'change',
      (nextState: AppStateStatus) => {
        const cameToForeground =
          appStateRef.current.match(/inactive|background/) &&
          nextState === 'active';
        appStateRef.current = nextState;

        if (cameToForeground && user?.clientCode) {
          // O store ignora a chamada se já houver modal em exibição.
          fetchCommunicationModals();
        }
      },
    );

    return () => subscription.remove();
  }, [user?.clientCode, fetchCommunicationModals]);

  return (
    <ActionSheetProvider>
      <SafeAreaProvider>
        <SafeAreaView style={styles.safeArea} edges={['top', 'bottom']}>
          <StatusBar
            animated={true}
            barStyle="dark-content"
            backgroundColor="white"
            translucent={false}
            showHideTransition="fade"
            hidden={false}
          />
          <View style={styles.container}>
            <AppModalHost
              showBuildProblem={showBuildProblem}
              showUpdate={showModal}
            />
            <RoutesTab />
            <LoadingScreen visible={isLoading} />
          </View>
          <GlobalAlert />
        </SafeAreaView>
      </SafeAreaProvider>
    </ActionSheetProvider>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: 'white',
  },
  container: {
    flex: 1,
  },
  loading: { flex: 1, justifyContent: 'center', alignItems: 'center' },

});

export default App;
